import React, { useEffect, ReactNode, useRef, useState, createContext, useContext } from 'react';
import { websocketManager, serviceManager } from "src/common/services";
import { ApiGatewayFactory } from 'src/common/services';
import { AI_PLAYGROUND_CONFIGS } from 'src/common/services';
import { SpinnerOverlay } from 'src/common';
import { Forbidden } from '../components/Forbidden';

interface UserContextType {
  userId: string | null;
}

const UserContext = createContext<UserContextType>({ userId: null });

export const useUser = () => useContext(UserContext);

interface AIPlaygroundProviderProps {
  children: ReactNode;
}

export const AIPlaygroundProvider: React.FC<AIPlaygroundProviderProps> = ({
  children,
}) => {
  const isInitialized = useRef(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Initialize services
  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    const initializeService = async () => {
      try {
        const manager = await serviceManager;
        const mainService = manager.getService();

        if (mainService) {
          const healthResponse = await mainService.getHealth();
          console.log('Health Check Response:', healthResponse);
          if (!healthResponse.data?.user_id || healthResponse.statusCode === 401 || healthResponse.statusCode === 403) {
            setStatusCode(healthResponse.statusCode);
            setIsForbidden(true);
            setIsLoading(false);
            return;
          }

          setUserId(healthResponse.data.user_id);
          websocketManager.initialize(mainService, healthResponse.data.user_id);

          setIsLoading(false);
        }
      } catch (error) {
        console.error('Service initialization failed:', error);
        setIsLoading(true);
      }
    };

    initializeService();
    isInitialized.current = true;

    // Cleanup on unmount
    return () => {
      websocketManager.disconnect();
    };
  }, []);

  if (isLoading) {
    return <SpinnerOverlay message="Initializing AI Playground..." />;
  }

  if (isForbidden) {
    return <Forbidden statusCode={statusCode!} />;
  }

  return (
    <UserContext.Provider value={{ userId }}>
      {children}
    </UserContext.Provider>
  );
};